import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  fetchUserById,
  logout,
  loadAuthToken,
  clearAuthToken,
  saveAuthToken,
  refreshAuth,
  updateAuthActivity
} from '../services/pocketbase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentTenantId, setCurrentTenantId] = useState(null);
  const [currentTenantName, setCurrentTenantName] = useState(null);
  const [currentImpersonatedTenantId, setCurrentImpersonatedTenantId] = useState(null);
  const [currentImpersonatedTenantName, setCurrentImpersonatedTenantName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Get effective tenant ID (impersonated or actual)
  const getEffectiveTenantId = () => {
    return currentImpersonatedTenantId || currentTenantId;
  };

  // Get effective tenant name
  const getEffectiveTenantName = () => {
    return currentImpersonatedTenantName || currentTenantName;
  };

  // Logout Handler
  const handleLogout = () => {
    setCurrentUser(null);
    setCurrentUserId(null);
    setCurrentUserEmail(null);
    setCurrentUserRole(null);
    setCurrentTenantId(null);
    setCurrentTenantName(null);
    setCurrentImpersonatedTenantId(null);
    setCurrentImpersonatedTenantName(null);
    setIsAuthenticated(false);
    
    // Clear PocketBase and localStorage
    logout(); 
    clearAuthToken();
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedAuth = loadAuthToken(); // Loads into pb.authStore and returns data
        
        if (storedAuth) {
          // Check for idle timeout immediately upon restore
          const now = Date.now();
          if (now - (storedAuth.lastActivity || 0) > IDLE_TIMEOUT_MS) {
            console.log('Session expired due to inactivity');
            handleLogout();
            return;
          }

          // Refresh token to verify validity and get fresh data
          // refreshAuth will automatically call saveAuthToken() to update timestamp if successful
          const userData = await refreshAuth(); 
          
          if (userData) {
            setCurrentUser(userData);
            setCurrentUserId(userData.id);
            setCurrentUserEmail(userData.email);
            setCurrentUserRole(userData.role);
            setCurrentTenantId(userData.tenant);
            setCurrentTenantName(userData.expand?.tenant?.business_name || '');
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        handleLogout(); // Ensure clean state if restore fails
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Idle Timer Effect
  useEffect(() => {
    if (!isAuthenticated) return;

    // Throttle user activity updates to localStorage to avoid performance hit
    let lastUpdate = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > 5000) { // Update max every 5 seconds
        lastUpdate = now;
        updateAuthActivity(); // Updates localStorage timestamp
      }
    };

    // Attach listeners
    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, handleActivity));

    // Check for idle timeout every minute
    const checkIdleInterval = setInterval(() => {
      const stored = localStorage.getItem('bpas_auth');
      if (stored) {
        try {
          const { lastActivity } = JSON.parse(stored);
          if (Date.now() - (lastActivity || 0) > IDLE_TIMEOUT_MS) {
            handleLogout();
          }
        } catch (e) {
          // If storage is corrupted, ignore or logout. Ignoring for robustness.
        }
      }
    }, 60000);

    return () => {
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, handleActivity));
      clearInterval(checkIdleInterval);
    };
  }, [isAuthenticated]);

  const handleLogin = async (loginData) => {
    try {
      // Fetch full user data with tenant relation
      const fullUserData = await fetchUserById(loginData.record.id);
      
      // Safety check: Check for archived tenant here as well
      if (fullUserData.expand?.tenant?.is_archived) {
        logout();
        clearAuthToken();
        throw new Error('Your account is no longer active. Please contact BPA Solutions.');
      }
      
      setCurrentUser(fullUserData);
      setCurrentUserId(fullUserData.id);
      setCurrentUserEmail(fullUserData.email);
      setCurrentUserRole(fullUserData.role);
      setCurrentTenantId(fullUserData.tenant);
      setCurrentTenantName(fullUserData.expand?.tenant?.business_name || '');
      setIsAuthenticated(true);
      
      // Persist to localStorage with timestamp
      saveAuthToken();
      
      return fullUserData;
    } catch (error) {
      if (error.message.includes('Your account is no longer active')) {
        throw error;
      }
      throw new Error('Failed to complete login process');
    }
  };

  const startImpersonation = (tenantId, tenantName) => {
    setCurrentImpersonatedTenantId(tenantId);
    setCurrentImpersonatedTenantName(tenantName);
  };

  const stopImpersonation = () => {
    setCurrentImpersonatedTenantId(null);
    setCurrentImpersonatedTenantName(null);
  };

  const value = {
    currentUser,
    currentUserId,
    currentUserEmail,
    currentUserRole,
    currentTenantId,
    currentTenantName,
    currentImpersonatedTenantId,
    currentImpersonatedTenantName,
    isLoading,
    isAuthenticated,
    getEffectiveTenantId,
    getEffectiveTenantName,
    handleLogin,
    handleLogout,
    startImpersonation,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};